import { renderTopbar } from '../components/topbar.js';
import {
  getSchoolDashboard, buySeats, setStudentGeneration, setFreeContributor, addTeacher, removeTeacher,
  addStudentEmail, addStudentEmailsBulk, removeStudentEmail, setStudentPrice,
  bindTeacherToCourse, unbindTeacherFromCourse, listCourseBindings,
  decideSchoolJoinRequest, startNewSchoolYear,
  addJoinReviewer, removeJoinReviewer, getJoinRequestQueue, getJoinRequestHistory,
} from '../lib/school.js';
import { getCourses } from '../lib/courses.js';
import { formatPaise, rechargeSchoolWallet } from '../lib/wallet.js';
import { friendlyError } from '../lib/friendlyError.js';
import { alertDialog, confirmDialog } from '../lib/dialog.js';
import { currentUser, setSchoolOwnerCached } from '../lib/auth.js';
import { watchActiveListings } from '../lib/marketplace.js';
import { subscribeSchoolToListing, unsubscribeSchoolFromListing, listMySchoolSubscriptions } from '../lib/schoolMarketplace.js';
import { navigate } from '../router.js';
import { PAYMENTS_ENABLED } from '../lib/featureFlags.js';

function formatListingPrice(n) {
  return Number(n || 0) <= 0 ? 'Free' : `₹${Number(n).toLocaleString('en-IN')}`;
}

// SheetJS is loaded lazily from a CDN only when a school actually uploads a
// roster file (same pattern as the Razorpay checkout script) — it never bloats
// the main bundle. Handles .xlsx/.xls/.csv. If a Content-Security-Policy is in
// place, allow https://cdnjs.cloudflare.com as a script source.
const XLSX_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
let xlsxScriptPromise = null;
function loadXLSX() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxScriptPromise) return xlsxScriptPromise;
  xlsxScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = XLSX_SRC;
    s.onload = () => resolve(window.XLSX);
    s.onerror = () => { xlsxScriptPromise = null; reject(new Error('Could not load the spreadsheet reader. Check your connection and try again.')); };
    document.head.appendChild(s);
  });
  return xlsxScriptPromise;
}

const BULK_EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// Extract every email found in the file, across all sheets and columns. Header
// rows and label columns (Name, Roll No, etc.) are ignored automatically —
// only cells that look like an email address are kept. De-duplicated, lowercased.
async function extractEmailsFromFile(file) {
  const XLSX = await loadXLSX();
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const seen = new Set();
  const emails = [];
  (wb.SheetNames || []).forEach((name) => {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false });
    rows.forEach((row) => {
      (row || []).forEach((cell) => {
        const v = String(cell == null ? '' : cell).trim().toLowerCase();
        if (BULK_EMAIL_RE.test(v) && !seen.has(v)) { seen.add(v); emails.push(v); }
      });
    });
  });
  return emails;
}

// ─────────────────────────────────────────────────────────────────────────
// School admin page (#/school) — visible ONLY to the account registered as a
// school's owner (enforced server-side by getSchoolDashboard). Under the
// reseller model the school:
//   • buys/renews student SEATS for a term (paid from its prepaid wallet);
//   • decides whether students may create content (billed to the school
//     wallet) or view only — a single toggle;
//   • designates teachers (teacher-free tier);
//   • sees its own wallet + ledger.
// There is no student discount or school revenue share anymore, and the
// school never reveals what it charges its own students to the platform.
// ─────────────────────────────────────────────────────────────────────────

export function renderSchool(container) {
  container.innerHTML = '';
  container.appendChild(renderTopbar('/school'));

  const main = document.createElement('div');
  main.className = 'main-full';
  main.innerHTML = `<div class="empty-state">Loading your school dashboard…</div>`;
  container.appendChild(main);

  async function load() {
    try {
      const data = await getSchoolDashboard();
      setSchoolOwnerCached(currentUser()?.uid, true);
      renderDashboard(main, data, load);
      return;
    } catch (err) {
      setSchoolOwnerCached(currentUser()?.uid, false);
    }
    // Not the owner — maybe a delegated join-request reviewer instead
    // (see addJoinReviewer). Their view is deliberately narrow: just the
    // join-request queue, nothing else on the dashboard.
    try {
      const queue = await getJoinRequestQueue();
      renderReviewerOnly(main, queue, load);
      return;
    } catch (err) { /* not a reviewer either — fall through to the trial pitch */ }
    main.innerHTML = `
      <div class="empty-state">
        <div style="font-size:2.5rem;">🏫</div>
        <h3>Bring Simustage to your school</h3>
        <p style="max-width:520px;margin:8px auto;">Start a free 3-month trial — your students get covered viewing, the marketplace, and every free animation. No card, no setup call.</p>
        <button class="btn btn-primary btn-lg" id="start-school-trial-btn" style="margin-top:6px;">Start your free trial</button>
        <p style="color:var(--text-dim);max-width:520px;margin:14px auto 0;font-size:0.85rem;">Already arranged a custom plan with our team? They'll set you up directly.</p>
      </div>`;
    main.querySelector('#start-school-trial-btn')?.addEventListener('click', () => navigate('/school/start'));
  }

  load();
  return () => {};
}

// A delegated join-request reviewer's whole view — approve/disallow +
// history only, none of the owner's wallet/seats/teachers/pricing controls.
function renderReviewerOnly(main, data, reload) {
  main.innerHTML = `
    <div class="main-full" style="max-width:720px;margin:0 auto;">
      <h1 style="margin-bottom:4px;">🏫 ${escapeHtml(data.schoolName || '')}</h1>
      <p style="color:var(--text-dim);margin-top:0;">You can review join requests for this school. Cycle #${data.academicYearSeq || 0}.</p>
      <div class="settings-panel">
        <h3>Join requests</h3>
        <div id="join-request-list" style="margin:8px 0;max-height:320px;overflow:auto;">
          ${(data.pendingJoinRequests || []).length === 0
            ? '<p style="color:var(--text-dim);font-size:0.86rem;">No pending requests.</p>'
            : (data.pendingJoinRequests || []).map((r) => `
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--border);">
                <span style="font-size:0.9rem;word-break:break-all;">${escapeHtml(r.email || r.uid)}</span>
                <span style="display:flex;gap:6px;flex-shrink:0;">
                  <button class="btn btn-sm" data-join-history="${escapeHtml(r.uid)}" data-join-email="${escapeHtml(r.email || r.uid)}">History</button>
                  <button class="btn btn-sm" data-decide-join="${escapeHtml(r.uid)}" data-decision="approve">Allow</button>
                  <button class="btn btn-sm" data-decide-join="${escapeHtml(r.uid)}" data-decision="disallow">Disallow</button>
                </span>
              </div>`).join('')}
        </div>
        <div id="join-request-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>
    </div>`;

  main.querySelectorAll('[data-decide-join]').forEach((el) => {
    el.addEventListener('click', async () => {
      const msg = main.querySelector('#join-request-msg');
      msg.style.color = 'var(--danger)';
      msg.textContent = '';
      main.querySelectorAll('[data-decide-join]').forEach((b) => { b.disabled = true; });
      try {
        await decideSchoolJoinRequest(el.getAttribute('data-decide-join'), el.getAttribute('data-decision'));
        await reload();
      } catch (err) {
        main.querySelectorAll('[data-decide-join]').forEach((b) => { b.disabled = false; });
        msg.textContent = friendlyError(err, 'Could not update that request.');
      }
    });
  });

  main.querySelectorAll('[data-join-history]').forEach((el) => {
    el.addEventListener('click', async () => {
      el.disabled = true;
      try {
        const events = await getJoinRequestHistory(el.getAttribute('data-join-history'));
        const email = el.getAttribute('data-join-email');
        const text = events.length === 0
          ? 'No history yet.'
          : events.map((e) => `${e.type} — ${fmtDate(e.at)}${e.by ? ` (by ${e.by})` : ''}`).join('\n');
        await alertDialog(text, { title: `History — ${email}` });
      } catch (err) {
        await alertDialog(friendlyError(err, 'Could not load history.'));
      } finally {
        el.disabled = false;
      }
    });
  });
}

function fmtDate(ms) {
  return ms ? new Date(ms).toLocaleDateString() : '—';
}

function renderDashboard(main, { school, transactions }, reload) {
  const seatsUsed = school.seatsUsed || 0;
  const seatsLicensed = school.seatsLicensed || 0;
  const seatsFree = Math.max(0, seatsLicensed - seatsUsed);
  const wholesale = school.seatWholesaleRupees ?? 0;
  const minCharge = school.minSchoolChargeRupees ?? 0;
  const termDays = school.seatTermDays ?? 365;
  const seatCostRupees = (q) => Math.max(q * wholesale, minCharge);

  // Self-serve trial banner + wallet top-up bounds.
  const DAY_MS = 24 * 60 * 60 * 1000;
  const trialDaysLeft = school.trialEndsAt ? Math.max(0, Math.ceil((school.trialEndsAt - Date.now()) / DAY_MS)) : null;
  const minTopup = school.minRechargeRupees ?? 10;
  const defaultTopup = Math.max(minTopup, wholesale || 0) || minTopup;
  const trialBanner = school.trial ? `
      <div class="settings-panel" style="grid-column:1/-1;border:1px solid var(--accent);">
        <h3 style="margin-top:0;">🎁 Free trial active</h3>
        <p style="margin:4px 0;">Up to <strong>${seatsLicensed.toLocaleString()}</strong> students can link your code for covered viewing, the marketplace, and every free animation.${trialDaysLeft != null ? ` <strong>${trialDaysLeft}</strong> day${trialDaysLeft === 1 ? '' : 's'} left.` : ''}</p>
        <p style="color:var(--text-dim);font-size:0.86rem;margin:4px 0 0;">Student content-creation is off during the trial. To turn it on, or to keep access after the trial, add funds below and buy seats.</p>
      </div>` : '';

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:8px;">
      <div>
        <h1 style="margin-bottom:4px;">🏫 ${escapeHtml(school.name)}</h1>
        <p style="color:var(--text-dim);margin-top:0;">School code: <strong>${escapeHtml(school.code)}</strong>
          ${school.enabled ? '<span class="pill">Active</span>' : '<span class="pill" style="background:var(--danger);color:#fff;">Disabled</span>'}
          ${school.seatActive ? '<span class="pill">Seats active</span>' : '<span class="pill" style="background:var(--danger);color:#fff;">No active seats</span>'}
        </p>
      </div>
    </div>

    <div class="settings-grid">
      ${trialBanner}
      <div class="settings-panel">
        <h3>Student seats</h3>
        <div class="admin-stat-grid">
          <div class="admin-stat"><span>Seats licensed</span><strong>${seatsLicensed.toLocaleString()}</strong></div>
          <div class="admin-stat"><span>Seats in use</span><strong>${seatsUsed.toLocaleString()}</strong></div>
          <div class="admin-stat"><span>Seats free</span><strong>${seatsFree.toLocaleString()}</strong></div>
          <div class="admin-stat"><span>Term ends</span><strong>${fmtDate(school.seatTermEndsAt)}</strong></div>
        </div>
        <p style="color:var(--text-dim);font-size:0.86rem;margin-top:10px;">Buying or renewing sets your total seats and adds a ${termDays}-day term on top of your current term (or from today if it has lapsed) at ${formatPaise(wholesale * 100)}/seat${minCharge > 0 ? ` (minimum ${formatPaise(minCharge * 100)} per purchase)` : ''}, charged from your school wallet. You can't buy fewer seats than you have linked students. What you charge your own students is up to you.</p>
        <div class="field" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
          <div><label>Seats</label><input type="number" id="seat-qty" min="1" step="1" value="${Math.max(seatsLicensed, 1)}" style="width:120px;" /></div>
          ${PAYMENTS_ENABLED ? '<button class="btn btn-primary" id="buy-seats-btn">Buy / renew seats</button>' : ''}
          <span id="seat-cost" style="color:var(--text-dim);font-size:0.86rem;"></span>
        </div>
        ${PAYMENTS_ENABLED ? '' : '<p style="color:var(--text-dim);font-size:0.85rem;margin-top:6px;">Buying seats isn\'t live yet during this pilot — contact us if you need more.</p>'}
        <div id="seat-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>

      <div class="settings-panel">
        <h3>School wallet</h3>
        <p style="font-size:2rem;font-weight:800;margin:6px 0;">${formatPaise(school.balancePaise)}</p>
        <p style="color:var(--text-dim);font-size:0.88rem;">This prepaid balance pays for seats and — if you enable it — your students' content creation. Top it up online any time.</p>
        ${PAYMENTS_ENABLED ? `
        <div class="field" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
          <div><label>Add funds (₹)</label><input type="number" id="school-topup-amt" min="${minTopup}" step="1" value="${defaultTopup}" style="width:140px;" /></div>
          <button class="btn btn-primary" id="school-topup-btn">Add funds</button>
        </div>` : '<p style="color:var(--text-dim);font-size:0.85rem;">Topping up isn\'t live yet during this pilot — contact us if you need funds added.</p>'}
        <div id="school-topup-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>

      <div class="settings-panel">
        <h3>Student invoicing</h3>
        <p style="color:var(--text-dim);font-size:0.86rem;">Set what YOUR school charges each student per term — you decide the amount (e.g. ₹500 or ₹600 per student), even though the platform's own charge is fixed at ${formatPaise(wholesale * 100)}. Every student you've added by email automatically receives their invoice the first time they sign in each term. On it, the amount above the platform's fixed charge is shown as <strong>"Instructor Charges"</strong>. All amounts are inclusive of taxes. Set to 0 to turn invoicing off.</p>
        <div class="field" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
          <div><label>Price per student (₹, incl. taxes)</label><input type="number" id="student-price" min="0" step="1" value="${school.studentPriceRupees ?? 0}" style="width:170px;" /></div>
          <button class="btn btn-primary" id="save-student-price-btn">Save price</button>
        </div>
        <div id="student-price-preview" style="color:var(--text-dim);font-size:0.85rem;margin-top:6px;"></div>
        <div id="student-price-msg" style="font-size:0.85rem;margin-top:4px;"></div>
      </div>

      <div class="settings-panel">
        <h3>Student content creation</h3>
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
          <input type="checkbox" id="gen-toggle" ${school.studentsCanGenerate ? 'checked' : ''} />
          <span>Let students create content (not just view)</span>
        </label>
        <p style="color:var(--text-dim);font-size:0.86rem;margin-top:8px;">When on, each student generation / Ask-AI is billed to your school wallet. When off, students can view assigned and purchased content but can't generate. Teachers can always create.</p>
        <div id="gen-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>

      <div class="settings-panel">
        <h3>Free-content network</h3>
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;">
          <input type="checkbox" id="free-contrib-toggle" ${school.isFreeContributor ? 'checked' : ''} />
          <span>Give free access to everything my school publishes, to every other school in this network</span>
        </label>
        <p style="color:var(--text-dim);font-size:0.86rem;margin-top:8px;">This is a two-way trade, not a discount: turning it on means any content your school publishes to the marketplace is free for other participating schools to subscribe to — and in exchange, your school gets free access to everything <em>every other</em> participating school publishes. It does nothing for content from schools that haven't opted in, and doesn't affect content sold by individual (non-school) creators.</p>
        <div id="free-contrib-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>

      <div class="settings-panel">
        <h3>Teachers</h3>
        ${(() => {
          const pool = Number(school.schoolFreeGenerationsPerMonth || 0);
          const tCount = (school.teachers || []).length;
          const share = tCount > 0 ? Math.floor(pool / tCount) : pool;
          return `<p style="color:var(--text-dim);font-size:0.86rem;">Your school gets <strong>${pool.toLocaleString()}</strong> free generations each month, shared equally by your teachers${tCount > 0 ? ` — with ${tCount} teacher${tCount === 1 ? '' : 's'} that's <strong>${share.toLocaleString()}</strong> each` : ''}. Beyond that, generations are billed to your school wallet — top it up to keep creating. Teachers don't use a student seat.</p>`;
        })()}
        <div id="teacher-list" style="margin:8px 0;">
          ${(school.teachers || []).length === 0
            ? '<p style="color:var(--text-dim);font-size:0.86rem;">No teachers yet.</p>'
            : (school.teachers || []).map((t) => `
              <div style="padding:4px 0;border-bottom:1px solid var(--border);">
                <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
                  <span style="font-size:0.9rem;">${escapeHtml(t.email || t.uid)}</span>
                  <span style="display:flex;gap:6px;flex-shrink:0;">
                    <button class="btn btn-sm" data-manage-courses="${escapeHtml(t.uid)}">Courses</button>
                    <button class="btn btn-sm" data-remove-teacher="${escapeHtml(t.uid)}">Remove</button>
                  </span>
                </div>
                <div class="teacher-courses-panel" data-teacher-courses="${escapeHtml(t.uid)}" hidden style="padding:8px 0 4px;"></div>
              </div>`).join('')}
        </div>
        <div class="field" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
          <div style="flex:1;min-width:180px;"><label>Teacher email</label><input type="email" id="teacher-email" placeholder="teacher@school.edu" /></div>
          <button class="btn" id="add-teacher-btn">Add teacher</button>
        </div>
        <div id="teacher-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>

      <div class="settings-panel">
        <h3>Join requests</h3>
        <p style="color:var(--text-dim);font-size:0.86rem;">Post your school code (<strong>${escapeHtml(school.code)}</strong>) anywhere your students will see it — they enter it themselves under Settings, and each request lands here for you to allow or disallow. Approving uses one seat, same as adding by email.</p>
        <div id="join-request-list" style="margin:8px 0;max-height:240px;overflow:auto;">
          ${(school.pendingJoinRequests || []).length === 0
            ? '<p style="color:var(--text-dim);font-size:0.86rem;">No pending requests.</p>'
            : (school.pendingJoinRequests || []).map((r) => `
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--border);">
                <span style="font-size:0.9rem;word-break:break-all;">${escapeHtml(r.email || r.uid)}</span>
                <span style="display:flex;gap:6px;flex-shrink:0;">
                  <button class="btn btn-sm" data-join-history="${escapeHtml(r.uid)}" data-join-email="${escapeHtml(r.email || r.uid)}">History</button>
                  <button class="btn btn-sm" data-decide-join="${escapeHtml(r.uid)}" data-decision="approve">Allow</button>
                  <button class="btn btn-sm" data-decide-join="${escapeHtml(r.uid)}" data-decision="disallow">Disallow</button>
                </span>
              </div>`).join('')}
        </div>
        <div id="join-request-msg" style="font-size:0.85rem;margin-top:6px;"></div>
        ${(school.approvedRequestStudents || []).length > 0 ? `
          <details style="margin-top:10px;">
            <summary style="cursor:pointer;color:var(--text-dim);font-size:0.86rem;">Approved this year (${(school.approvedRequestStudents || []).length})</summary>
            <div style="margin:8px 0;max-height:200px;overflow:auto;">
              ${(school.approvedRequestStudents || []).map((r) => `
                <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--border);">
                  <span style="font-size:0.9rem;word-break:break-all;">${escapeHtml(r.email || r.uid)}</span>
                  <span style="display:flex;gap:6px;flex-shrink:0;">
                    <button class="btn btn-sm" data-join-history="${escapeHtml(r.uid)}" data-join-email="${escapeHtml(r.email || r.uid)}">History</button>
                    <a class="btn btn-sm" style="text-decoration:none;" href="#/school/student/${encodeURIComponent(r.email || '')}">View scores</a>
                  </span>
                </div>`).join('')}
            </div>
          </details>` : ''}
        <details style="margin-top:10px;">
          <summary style="cursor:pointer;color:var(--text-dim);font-size:0.86rem;">Reviewers who can also allow/disallow (${(school.joinReviewers || []).length})</summary>
          <p style="color:var(--text-dim);font-size:0.85rem;margin-top:6px;">A reviewer can only approve/disallow join requests here — nothing else on this dashboard (no wallet, seats, teachers, or pricing).</p>
          <div id="join-reviewer-list" style="margin:8px 0;">
            ${(school.joinReviewers || []).length === 0
              ? '<p style="color:var(--text-dim);font-size:0.86rem;">Only you can review requests right now.</p>'
              : (school.joinReviewers || []).map((r) => `
                <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--border);">
                  <span style="font-size:0.9rem;word-break:break-all;">${escapeHtml(r.email || r.uid)}</span>
                  <button class="btn btn-sm" data-remove-join-reviewer="${escapeHtml(r.uid)}">Remove</button>
                </div>`).join('')}
          </div>
          <div class="field" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
            <div style="flex:1;min-width:180px;"><label>Reviewer email</label><input type="email" id="join-reviewer-email" placeholder="reviewer@school.edu" /></div>
            <button class="btn" id="add-join-reviewer-btn">Add reviewer</button>
          </div>
        </details>
        <div style="margin-top:12px;border-top:1px dashed var(--border);padding-top:12px;">
          <p style="color:var(--text-dim);font-size:0.86rem;">Current year: cycle #${school.academicYearSeq || 0}${school.academicYearStartedAt ? `, started ${fmtDate(school.academicYearStartedAt)}` : ''}. Since you collect payment yearly, start a new year when it's time to renew — every student who joined by code will need to re-request and be re-approved. Students you added by email below are not affected.</p>
          <button class="btn btn-sm" id="start-new-year-btn">Start new year</button>
        </div>
      </div>

      <div class="settings-panel">
        <h3>Students</h3>
        <p style="color:var(--text-dim);font-size:0.86rem;">Add students by email — each is covered automatically the moment they sign in, no code to type. Each uses one seat. Remove an email and that student goes back to their own wallet.</p>
        <div id="student-list" style="margin:8px 0;max-height:240px;overflow:auto;">
          ${(school.studentEmails || []).length === 0
            ? '<p style="color:var(--text-dim);font-size:0.86rem;">No students added yet.</p>'
            : (school.studentEmails || []).map((em) => `
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;border-bottom:1px solid var(--border);">
                <span style="font-size:0.9rem;word-break:break-all;">${escapeHtml(em)}</span>
                <span style="display:flex;gap:6px;flex-shrink:0;">
                  <a class="btn btn-sm" style="text-decoration:none;" href="#/school/student/${encodeURIComponent(em)}">View scores</a>
                  <button class="btn btn-sm" data-remove-student="${escapeHtml(em)}">Remove</button>
                </span>
              </div>`).join('')}
        </div>
        <div class="field" style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
          <div style="flex:1;min-width:180px;"><label>Student email</label><input type="email" id="student-email" placeholder="student@example.com" /></div>
          <button class="btn" id="add-student-btn">Add student</button>
        </div>
        <div id="student-msg" style="font-size:0.85rem;margin-top:6px;"></div>
        <div style="margin-top:12px;border-top:1px dashed var(--border);padding-top:12px;">
          <label style="font-size:0.86rem;color:var(--text-dim);display:block;margin-bottom:6px;">Bulk add from Excel/CSV — the first row is treated as column titles and the email column is detected automatically. If the list needs more seats than you have free, nothing is uploaded.</label>
          <div class="field" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
            <input type="file" id="student-file" accept=".xlsx,.xls,.csv" />
            <button class="btn" id="upload-students-btn">Upload list</button>
          </div>
          <div id="student-bulk-msg" style="font-size:0.85rem;margin-top:6px;"></div>
        </div>
      </div>

      <div class="settings-panel" style="grid-column:1/-1;">
        <h3>Library subscriptions</h3>
        <p style="color:var(--text-dim);font-size:0.86rem;">Subscribe your school to a course/topic from the Library — it becomes free, view-only, and automatically visible to every one of your students and teachers, tagged "Subscribed by School". Your school can never resell or re-list this content.</p>
        <div id="school-sub-list" style="margin:8px 0;"></div>
        <h4 style="margin:16px 0 6px;">Browse the Library</h4>
        <div id="school-browse-list" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:10px;"></div>
        <div id="school-sub-msg" style="font-size:0.85rem;margin-top:6px;"></div>
      </div>

      <div class="settings-panel" style="grid-column:1/-1;">
        <h3>Wallet ledger</h3>
        ${transactions.length === 0
          ? `<div class="empty-state"><div style="font-size:2rem;">🧾</div><p>No wallet activity yet.</p></div>`
          : `<div style="overflow:auto;"><table class="admin-table">
              <thead><tr><th>When</th><th>Activity</th><th style="text-align:right;">Amount</th><th style="text-align:right;">Balance</th></tr></thead>
              <tbody>
                ${transactions.map((t) => `<tr>
                  <td>${t.createdAt ? new Date(t.createdAt).toLocaleString() : ''}</td>
                  <td>${escapeHtml(t.note || t.type || '')}</td>
                  <td style="text-align:right;color:${(t.amountPaise || 0) >= 0 ? 'var(--success)' : 'var(--danger)'};font-weight:700;">${(t.amountPaise || 0) >= 0 ? '+' : '−'}${formatPaise(Math.abs(t.amountPaise || 0))}</td>
                  <td style="text-align:right;">${formatPaise(t.balanceAfterPaise || 0)}</td>
                </tr>`).join('')}
              </tbody>
            </table></div>`}
      </div>
    </div>
  `;

  wireMarketplaceSubscriptions(main);

  // ── Wire controls ──────────────────────────────────────────────────────
  const seatQty = main.querySelector('#seat-qty');
  const seatCost = main.querySelector('#seat-cost');
  const updateSeatCost = () => {
    const q = Math.max(0, Math.trunc(Number(seatQty.value) || 0));
    if (q <= 0) { seatCost.textContent = ''; return; }
    const cost = seatCostRupees(q);
    const floored = minCharge > 0 && cost > q * wholesale;
    seatCost.textContent = `= ${formatPaise(cost * 100)} for ${termDays} days${floored ? ' (minimum applied)' : ''}`;
  };
  seatQty.addEventListener('input', updateSeatCost);
  updateSeatCost();

  const topupBtn = main.querySelector('#school-topup-btn');
  topupBtn?.addEventListener('click', async () => {
    const amtEl = main.querySelector('#school-topup-amt');
    const tmsg = main.querySelector('#school-topup-msg');
    const amt = Math.trunc(Number(amtEl.value) || 0);
    tmsg.style.color = 'var(--danger)';
    if (!amt || amt <= 0) { tmsg.textContent = 'Enter an amount greater than zero.'; return; }
    topupBtn.disabled = true; topupBtn.textContent = 'Opening payment…';
    try {
      await rechargeSchoolWallet(amt);
      await reload();
    } catch (err) {
      tmsg.textContent = friendlyError(err, 'Could not add funds.');
      topupBtn.disabled = false; topupBtn.textContent = 'Add funds';
    }
  });

  main.querySelector('#buy-seats-btn')?.addEventListener('click', async () => {
    const btn = main.querySelector('#buy-seats-btn');
    const msg = main.querySelector('#seat-msg');
    const q = Math.trunc(Number(seatQty.value) || 0);
    msg.style.color = 'var(--danger)';
    if (!q || q <= 0) { msg.textContent = 'Enter a seat count greater than zero.'; return; }
    btn.disabled = true; btn.textContent = 'Processing…';
    try {
      await buySeats(q);
      await reload();
    } catch (err) {
      msg.textContent = friendlyError(err, 'Could not buy seats.');
      btn.disabled = false; btn.textContent = 'Buy / renew seats';
    }
  });

  // Student invoicing — live split preview + save.
  const priceInput = main.querySelector('#student-price');
  const pricePreview = main.querySelector('#student-price-preview');
  const updatePricePreview = () => {
    const p = Math.max(0, Number(priceInput.value) || 0);
    if (p <= 0) { pricePreview.textContent = 'Invoicing is off — students receive no invoice.'; return; }
    const instructor = Math.max(0, p - wholesale);
    pricePreview.textContent = `Each invoice will show: Platform charges ${formatPaise(Math.min(p, wholesale) * 100)} + Instructor Charges ${formatPaise(instructor * 100)} = Total ${formatPaise(p * 100)} (inclusive of all taxes).`;
  };
  priceInput.addEventListener('input', updatePricePreview);
  updatePricePreview();
  main.querySelector('#save-student-price-btn').addEventListener('click', async () => {
    const btn = main.querySelector('#save-student-price-btn');
    const msg = main.querySelector('#student-price-msg');
    const p = Number(priceInput.value);
    msg.style.color = 'var(--danger)';
    if (!Number.isFinite(p) || p < 0) { msg.textContent = 'Enter a valid amount in rupees.'; return; }
    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      const res = await setStudentPrice(p > 0 ? p : null);
      msg.style.color = 'var(--success)';
      msg.textContent = res.studentPriceRupees
        ? `Saved — rostered students will be invoiced ${formatPaise(res.studentPriceRupees * 100)} (incl. taxes) on their first sign-in each term.`
        : 'Saved — student invoicing is now off.';
    } catch (err) {
      msg.textContent = friendlyError(err, 'Could not save the price.');
    } finally {
      btn.disabled = false; btn.textContent = 'Save price';
    }
  });

  main.querySelector('#gen-toggle').addEventListener('change', async (e) => {
    const msg = main.querySelector('#gen-msg');
    const enabled = e.target.checked;
    e.target.disabled = true;
    try {
      await setStudentGeneration(enabled);
      msg.style.color = 'var(--success)';
      msg.textContent = enabled ? 'Students can now create content.' : 'Students are now view-only.';
    } catch (err) {
      e.target.checked = !enabled;
      msg.style.color = 'var(--danger)';
      msg.textContent = friendlyError(err, 'Could not update the setting.');
    } finally {
      e.target.disabled = false;
    }
  });

  main.querySelector('#free-contrib-toggle').addEventListener('change', async (e) => {
    const msg = main.querySelector('#free-contrib-msg');
    const enabled = e.target.checked;
    if (enabled) {
      const ok = await confirmDialog(
        "Turning this on makes everything your school publishes to the marketplace free for other participating schools to subscribe to. In exchange, your school gets free access to everything every other participating school publishes. It's a two-way trade — if your school stops contributing free content, turn this back off.",
        { title: 'Join the free-content network?', okText: 'Turn on', cancelText: 'Cancel' },
      );
      if (!ok) { e.target.checked = false; return; }
    }
    e.target.disabled = true;
    try {
      await setFreeContributor(enabled);
      msg.style.color = 'var(--success)';
      msg.textContent = enabled
        ? "You're now part of the free-content network."
        : "You've left the free-content network — your content and prices are back to normal.";
    } catch (err) {
      e.target.checked = !enabled;
      msg.style.color = 'var(--danger)';
      msg.textContent = friendlyError(err, 'Could not update the setting.');
    } finally {
      e.target.disabled = false;
    }
  });

  main.querySelector('#add-teacher-btn').addEventListener('click', async () => {
    const btn = main.querySelector('#add-teacher-btn');
    const msg = main.querySelector('#teacher-msg');
    const email = main.querySelector('#teacher-email').value.trim();
    msg.style.color = 'var(--danger)';
    if (!email) { msg.textContent = 'Enter a teacher email.'; return; }
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      await addTeacher(email);
      await reload();
    } catch (err) {
      msg.textContent = friendlyError(err, 'Could not add that teacher.');
      btn.disabled = false; btn.textContent = 'Add teacher';
    }
  });

  main.querySelectorAll('[data-remove-teacher]').forEach((el) => {
    el.addEventListener('click', async () => {
      el.disabled = true;
      try {
        await removeTeacher(el.getAttribute('data-remove-teacher'));
        await reload();
      } catch (err) {
        el.disabled = false;
        const msg = main.querySelector('#teacher-msg');
        msg.style.color = 'var(--danger)';
        msg.textContent = friendlyError(err, 'Could not remove that teacher.');
      }
    });
  });

  // Per-teacher course binding — which of the owner's own courses a teacher
  // can track quiz results for (see functions/schools.js bindTeacherToCourse).
  main.querySelectorAll('[data-manage-courses]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const teacherUid = btn.getAttribute('data-manage-courses');
      const panel = main.querySelector(`[data-teacher-courses="${teacherUid}"]`);
      if (!panel) return;
      if (!panel.hidden) { panel.hidden = true; return; }
      panel.hidden = false;
      panel.innerHTML = '<p style="color:var(--text-dim);font-size:0.85rem;">Loading…</p>';
      try {
        const [courses, bindings] = await Promise.all([getCourses(currentUser().uid), listCourseBindings()]);
        const boundCourseIds = new Set(bindings.filter((b) => (b.teacherUids || []).includes(teacherUid)).map((b) => b.courseId));
        panel.innerHTML = courses.length === 0
          ? '<p style="color:var(--text-dim);font-size:0.85rem;">You have no courses yet.</p>'
          : courses.map((c) => `
            <label style="display:flex;align-items:center;gap:8px;font-size:0.86rem;padding:2px 0;cursor:pointer;">
              <input type="checkbox" data-bind-course="${escapeHtml(c.id)}" ${boundCourseIds.has(c.id) ? 'checked' : ''} />
              ${c.emoji || '📘'} ${escapeHtml(c.title)}
            </label>`).join('');
        panel.querySelectorAll('[data-bind-course]').forEach((cb) => {
          cb.addEventListener('change', async () => {
            const courseId = cb.getAttribute('data-bind-course');
            cb.disabled = true;
            try {
              if (cb.checked) await bindTeacherToCourse(teacherUid, courseId);
              else await unbindTeacherFromCourse(teacherUid, courseId);
            } catch (err) {
              cb.checked = !cb.checked;
              alertDialog(friendlyError(err, 'Could not update that binding.'));
            } finally {
              cb.disabled = false;
            }
          });
        });
      } catch (err) {
        panel.innerHTML = `<p style="color:var(--danger);font-size:0.85rem;">${escapeHtml(friendlyError(err, 'Could not load courses.'))}</p>`;
      }
    });
  });

  main.querySelectorAll('[data-decide-join]').forEach((el) => {
    el.addEventListener('click', async () => {
      const msg = main.querySelector('#join-request-msg');
      msg.style.color = 'var(--danger)';
      msg.textContent = '';
      main.querySelectorAll('[data-decide-join]').forEach((b) => { b.disabled = true; });
      try {
        await decideSchoolJoinRequest(el.getAttribute('data-decide-join'), el.getAttribute('data-decision'));
        await reload();
      } catch (err) {
        main.querySelectorAll('[data-decide-join]').forEach((b) => { b.disabled = false; });
        msg.textContent = friendlyError(err, 'Could not update that request.');
      }
    });
  });

  main.querySelectorAll('[data-join-history]').forEach((el) => {
    el.addEventListener('click', async () => {
      el.disabled = true;
      try {
        const events = await getJoinRequestHistory(el.getAttribute('data-join-history'));
        const email = el.getAttribute('data-join-email');
        const text = events.length === 0
          ? 'No history yet.'
          : events.map((e) => `${e.type} — ${fmtDate(e.at)}${e.by ? ` (by ${e.by})` : ''}`).join('\n');
        await alertDialog(text, { title: `History — ${email}` });
      } catch (err) {
        await alertDialog(friendlyError(err, 'Could not load history.'));
      } finally {
        el.disabled = false;
      }
    });
  });

  main.querySelector('#add-join-reviewer-btn')?.addEventListener('click', async () => {
    const btn = main.querySelector('#add-join-reviewer-btn');
    const msg = main.querySelector('#join-request-msg');
    const email = main.querySelector('#join-reviewer-email').value.trim();
    msg.style.color = 'var(--danger)';
    if (!email) { msg.textContent = 'Enter a reviewer email.'; return; }
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      await addJoinReviewer(email);
      await reload();
    } catch (err) {
      msg.textContent = friendlyError(err, 'Could not add that reviewer.');
      btn.disabled = false; btn.textContent = 'Add reviewer';
    }
  });

  main.querySelectorAll('[data-remove-join-reviewer]').forEach((el) => {
    el.addEventListener('click', async () => {
      el.disabled = true;
      try {
        await removeJoinReviewer(el.getAttribute('data-remove-join-reviewer'));
        await reload();
      } catch (err) {
        el.disabled = false;
        const msg = main.querySelector('#join-request-msg');
        msg.style.color = 'var(--danger)';
        msg.textContent = friendlyError(err, 'Could not remove that reviewer.');
      }
    });
  });

  main.querySelector('#start-new-year-btn')?.addEventListener('click', async () => {
    const btn = main.querySelector('#start-new-year-btn');
    const msg = main.querySelector('#join-request-msg');
    const ok = await confirmDialog('Start a new year? Every student who joined by code will need to re-request and be re-approved. Students you added by email are not affected.');
    if (!ok) return;
    btn.disabled = true; btn.textContent = 'Starting…';
    try {
      await startNewSchoolYear();
      await reload();
    } catch (err) {
      msg.style.color = 'var(--danger)';
      msg.textContent = friendlyError(err, 'Could not start a new year.');
      btn.disabled = false; btn.textContent = 'Start new year';
    }
  });

  main.querySelector('#add-student-btn').addEventListener('click', async () => {
    const btn = main.querySelector('#add-student-btn');
    const msg = main.querySelector('#student-msg');
    const email = main.querySelector('#student-email').value.trim();
    msg.style.color = 'var(--danger)';
    if (!email) { msg.textContent = 'Enter a student email.'; return; }
    btn.disabled = true; btn.textContent = 'Adding…';
    try {
      await addStudentEmail(email);
      await reload();
    } catch (err) {
      msg.textContent = friendlyError(err, 'Could not add that student.');
      btn.disabled = false; btn.textContent = 'Add student';
    }
  });

  main.querySelectorAll('[data-remove-student]').forEach((el) => {
    el.addEventListener('click', async () => {
      el.disabled = true;
      try {
        await removeStudentEmail(el.getAttribute('data-remove-student'));
        await reload();
      } catch (err) {
        el.disabled = false;
        const msg = main.querySelector('#student-msg');
        msg.style.color = 'var(--danger)';
        msg.textContent = friendlyError(err, 'Could not remove that student.');
      }
    });
  });

  const uploadBtn = main.querySelector('#upload-students-btn');
  uploadBtn?.addEventListener('click', async () => {
    const fileEl = main.querySelector('#student-file');
    const bmsg = main.querySelector('#student-bulk-msg');
    const file = fileEl?.files?.[0];
    bmsg.style.color = 'var(--danger)';
    if (!file) { bmsg.textContent = 'Choose an .xlsx, .xls or .csv file first.'; return; }
    uploadBtn.disabled = true;
    uploadBtn.textContent = 'Reading…';
    try {
      const emails = await extractEmailsFromFile(file);
      if (emails.length === 0) {
        bmsg.textContent = 'No email addresses were found in that file.';
        uploadBtn.disabled = false; uploadBtn.textContent = 'Upload list';
        return;
      }
      // Client-side over-capacity pre-check (the server re-checks and is the
      // real guard): if the NEW students exceed free seats, stop and inform —
      // don't upload anything.
      const existing = new Set((school.studentEmails || []).map((e) => String(e).toLowerCase()));
      const teacherE = new Set((school.teachers || []).map((t) => String(t.email || '').toLowerCase()));
      const newOnes = emails.filter((e) => !existing.has(e) && !teacherE.has(e));
      const available = Math.max(0, seatsLicensed - seatsUsed);
      if (newOnes.length > available) {
        bmsg.textContent = `This list needs ${newOnes.length} new seat${newOnes.length === 1 ? '' : 's'} but only ${available} ${available === 1 ? 'is' : 'are'} free (of ${seatsLicensed} licensed). Buy more seats or shorten the list — nothing was uploaded.`;
        uploadBtn.disabled = false; uploadBtn.textContent = 'Upload list';
        return;
      }
      uploadBtn.textContent = 'Uploading…';
      const res = await addStudentEmailsBulk(emails);
      const parts = [`Added ${res.added} student${res.added === 1 ? '' : 's'}`];
      if (res.skippedExisting) parts.push(`${res.skippedExisting} already on the list`);
      if (res.skippedTeachers) parts.push(`${res.skippedTeachers} were teachers`);
      await reload();
      alertDialog(`${parts.join(', ')}.`, { title: 'Roster updated' });
    } catch (err) {
      bmsg.textContent = friendlyError(err, 'Could not upload the list.');
      uploadBtn.disabled = false; uploadBtn.textContent = 'Upload list';
    }
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Marketplace ↔ school subscription panel — see functions/schoolMarketplace.js
// for the full model. Browse is the same live `marketplace/` Firestore read
// every learner's Browse tab uses (src/lib/marketplace.js watchActiveListings);
// subscribing/unsubscribing and the school's current subscription list go
// through the school-specific callables in src/lib/schoolMarketplace.js.
function wireMarketplaceSubscriptions(main) {
  const subListEl = main.querySelector('#school-sub-list');
  const browseListEl = main.querySelector('#school-browse-list');
  const msgEl = main.querySelector('#school-sub-msg');
  if (!subListEl || !browseListEl) return;

  let subscriptions = [];
  let listings = [];

  function renderSubs() {
    subListEl.innerHTML = subscriptions.length === 0
      ? '<p style="color:var(--text-dim);font-size:0.86rem;">Not subscribed to anything yet.</p>'
      : subscriptions.map((s) => `
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border);">
          <span style="font-size:0.9rem;">${s.emoji || '📘'} ${escapeHtml(s.title)} <span class="pill" style="font-size:0.7rem;">view-only</span>${s.description ? `<br><span style="color:var(--text-dim);font-size:0.82rem;">${escapeHtml(s.description)}</span>` : ''}</span>
          <button class="btn btn-sm" data-unsub="${escapeHtml(s.listingId)}">Unsubscribe</button>
        </div>`).join('');
  }

  // Same guard as the individual Browse tab (see hasApprovedContent in
  // views/marketplace.js): a listing can exist and even be status:'active'
  // before the seller has ever clicked "Approve current version". Showing a
  // Subscribe button for one of those led straight to a 400 from
  // subscribeSchoolToListing ("The seller has not approved any content for
  // distribution yet.") with no useful feedback — filter them out instead,
  // same as buyers already never see them.
  function hasApprovedContent(l) {
    return l.type === 'topic' ? !!l.approvedVersion : !!(l.approvedVersions && Object.keys(l.approvedVersions).length);
  }

  function renderBrowse() {
    const subscribedIds = new Set(subscriptions.map((s) => s.listingId));
    const available = listings.filter(hasApprovedContent);
    browseListEl.innerHTML = available.map((l) => `
      <div class="settings-panel" style="margin:0;padding:12px;">
        <div style="font-size:0.9rem;font-weight:700;">${l.emoji || '📘'} ${escapeHtml(l.title)}</div>
        <div style="color:var(--text-dim);font-size:0.82rem;margin:4px 0;">${escapeHtml(l.courseTitle || '')}</div>
        <div style="font-size:0.85rem;margin-bottom:8px;">${escapeHtml(l.description || 'No description')}</div>
        <div style="font-size:0.85rem;margin-bottom:8px;">${formatListingPrice(l.viewPrice)} · view-only</div>
        <button class="btn btn-sm btn-primary" data-subscribe="${l.id}" ${subscribedIds.has(l.id) ? 'disabled' : ''}>${subscribedIds.has(l.id) ? 'Subscribed' : 'Subscribe'}</button>
      </div>`).join('') || '<p style="color:var(--text-dim);font-size:0.86rem;">No listings available right now.</p>';

    browseListEl.querySelectorAll('[data-subscribe]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = 'Subscribing…';
        msgEl.textContent = '';
        try {
          await subscribeSchoolToListing(btn.dataset.subscribe);
          await refreshSubs();
          msgEl.textContent = 'Subscribed — visible to your school now.';
        } catch (err) {
          msgEl.textContent = friendlyError(err, 'Could not subscribe.');
          btn.disabled = false;
          btn.textContent = 'Subscribe';
        }
      });
    });
  }

  async function refreshSubs() {
    try {
      const data = await listMySchoolSubscriptions();
      subscriptions = data.subscriptions || [];
    } catch (err) {
      subscriptions = [];
    }
    renderSubs();
    renderBrowse();
    subListEl.querySelectorAll('[data-unsub]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        try {
          await unsubscribeSchoolFromListing(btn.dataset.unsub);
          await refreshSubs();
        } catch (err) {
          msgEl.textContent = friendlyError(err, 'Could not unsubscribe.');
          btn.disabled = false;
        }
      });
    });
  }

  watchActiveListings((active) => {
    listings = active;
    renderBrowse();
  });
  refreshSubs();
}
